import React, { useState } from "react";
import { LayoutTemplate, Check, Loader2 } from "lucide-react";
import api from "../lib/api";
import { fontCss } from "../lib/beaconStyles";
import { TEMPLATES, TEMPLATE_LOOKS, TEMPLATE_EXAMPLE_TEXT, TEMPLATE_DETAILS, STANDARD_LOOK, lookFields } from "../lib/beaconTemplates";

const LookCard = ({ l, on, onPick }) => (
  <button type="button" onClick={onPick} data-testid={`template-look-${l.key}`}
    className={`text-left border p-3 transition-colors ${on ? "border-[#14A89A] bg-[#14A89A]/10" : "border-white/10 hover:border-white/25"}`}>
    <div className="h-14 mb-2 flex items-end gap-1.5 p-2 border border-white/15" style={{ background: l.swatch[0] }}>
      <span className="text-lg leading-none" style={{ fontFamily: fontCss(l.heading_font), color: l.swatch[1] }}>Aa</span>
      <span className="w-2.5 h-2.5 rounded-full mb-0.5" style={{ background: l.swatch[2] }} />
    </div>
    <div className="font-body text-sm text-white flex items-center gap-1.5">{on && <Check size={13} className="text-[#14A89A]" />}{l.label}</div>
    <div className="font-body text-[11px] text-zinc-500 leading-snug">{l.note}</div>
  </button>
);

// Shown above the editing tools. Folds into a small bar once a template is chosen.
// What each template includes, so creators can choose before setting anything up.
const TemplateDetails = ({ tpl }) => {
  const d = TEMPLATE_DETAILS[tpl];
  if (!d) return null;
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-4 mb-4 fade-up" key={tpl} data-testid={`template-details-${tpl}`}>
      <div className="font-body text-xs text-zinc-400 mb-2"><span className="text-white font-semibold">Best for: </span>{d.best}</div>
      <ul className="space-y-1.5 mb-2">
        {d.gets.map((g) => <li key={g} className="flex items-start gap-2 font-body text-xs text-zinc-300"><Check size={13} className="text-[#14A89A] shrink-0 mt-px" />{g}</li>)}
      </ul>
      <p className="font-body text-[11px] text-zinc-500">{d.skip} Every template includes your Hearth, live sessions, paid or free memberships and encrypted content.</p>
    </div>
  );
};

export const BeaconTemplatePicker = ({ beacon, updateForm, onApplied }) => {
  const [open, setOpen] = useState(!beacon.template);
  const [tpl, setTpl] = useState(beacon.template || "artist");
  const initLook = (t) => { const ls = TEMPLATE_LOOKS[t] || []; return (ls.find((l) => l.page_theme === beacon.page_theme) || ls[0])?.key; };
  const [look, setLook] = useState(initLook(beacon.template || "artist"));
  const looks = TEMPLATE_LOOKS[tpl];
  const [examples, setExamples] = useState(true);
  const [busy, setBusy] = useState(false);
  const current = TEMPLATES.find((t) => t.key === beacon.template);

  if (!open) return (
    <div className="border border-white/10 bg-[#121212] px-4 py-3 mb-6 flex items-center justify-between gap-3" data-testid="template-bar">
      <span className="font-body text-sm text-zinc-300 flex items-center gap-2"><LayoutTemplate size={15} className="text-[#14A89A]" />Template: <span className="text-white">{current?.label || "Freeform"}</span></span>
      <button onClick={() => setOpen(true)} data-testid="template-change-btn" className="font-body text-xs text-[#14A89A] hover:underline">Change template</button>
    </div>
  );

  const apply = async () => {
    setBusy(true);
    try {
      const l = looks?.find((x) => x.key === look);
      const fields = l ? { ...lookFields(l), template: tpl } : { ...lookFields(STANDARD_LOOK), template: tpl };
      await api.put("/beacon/mine", fields);
      updateForm(fields);
      if (TEMPLATE_EXAMPLE_TEXT[tpl] && examples) await api.post("/beacon/mine/template-examples", { template: tpl });
      await onApplied();
      setOpen(false);
      if (looks) setTimeout(() => window.dispatchEvent(new CustomEvent("ss-template-tour", { detail: tpl })), 400);
    } catch (e) { window.alert(e?.response?.data?.detail || "Couldn't apply the template, please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#14A89A]/30 bg-[#121212] p-5 mb-6" data-testid="template-picker">
      <h2 className="font-body text-lg text-white flex items-center gap-2 mb-1"><LayoutTemplate size={17} className="text-[#14A89A]" /> Pick a template</h2>
      <p className="font-body text-sm text-zinc-400 mb-4">Tap each one to see what it includes, so you can pick what fits your work. You can change it anytime, and your existing sections and lessons always stay as they are.</p>
      <div className="grid sm:grid-cols-3 gap-2.5 mb-4">
        {TEMPLATES.map((t) => (
          <button key={t.key} type="button" disabled={t.soon} onClick={() => { setTpl(t.key); setLook(initLook(t.key)); }} data-testid={`template-option-${t.key}`}
            className={`text-left border p-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${tpl === t.key ? "border-[#14A89A] bg-[#14A89A]/10" : "border-white/10 hover:border-white/25"}`}>
            <div className="font-body text-sm text-white flex items-center gap-2">{t.label}{t.soon && <span className="text-[9px] uppercase tracking-[0.15em] border border-white/15 text-zinc-400 px-1.5 py-0.5">Coming soon</span>}</div>
            <div className="font-body text-[11px] text-zinc-500 mt-0.5">{t.note}</div>
          </button>
        ))}
      </div>
      <TemplateDetails tpl={tpl} />
      {looks && <>
        <div className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2">Choose a look</div>
        <div className="grid sm:grid-cols-3 gap-2.5 mb-3">{looks.map((l) => <LookCard key={l.key} l={l} on={look === l.key} onPick={() => setLook(l.key)} />)}</div>
        {TEMPLATE_EXAMPLE_TEXT[tpl] && <label className="flex items-start gap-2 font-body text-sm text-zinc-300 mb-1"><input type="checkbox" checked={examples} onChange={(e) => setExamples(e.target.checked)} data-testid="template-examples-toggle" className="accent-[#14A89A] mt-1" />
          <span data-testid="template-examples-text">{TEMPLATE_EXAMPLE_TEXT[tpl]}</span></label>}
        <p className="font-body text-[11px] text-zinc-500 mb-4">The look changes your fonts, colours and page background. You can fine-tune everything afterward under "Illuminate your space".</p>
      </>}
      <div className="flex gap-2">
        <button onClick={apply} disabled={busy} data-testid="template-apply-btn" className="flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-sm disabled:opacity-40">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Use this template</button>
        {beacon.template && <button onClick={() => setOpen(false)} data-testid="template-cancel-btn" className="border border-white/15 text-zinc-300 px-4 py-2 font-body text-sm hover:bg-white/5">Cancel</button>}
      </div>
    </div>
  );
};
